export const MAX_RATE_LCC = 12

const EPS = 1e-10

function round2(value) {
  return Math.round(Number(value) * 100) / 100
}

/** Mensualité constante : M = P × i / (1 − (1+i)^(−n)) */
function annuityPayment(principal, monthlyRate, months) {
  if (principal <= EPS) return 0
  if (Math.abs(monthlyRate) <= EPS) return principal / months
  const growth = (1 + monthlyRate) ** months
  return principal * ((monthlyRate * growth) / (growth - 1))
}

/** NPV emprunteur : +C − Σ mensualités − VR actualisée (taux mensuel t). */
function npvBorrower(monthlyRate, financedAmount, paymentPerMonth, months, residualValue) {
  let npv = financedAmount
  for (let m = 1; m <= months; m++) {
    npv -= paymentPerMonth / (1 + monthlyRate) ** m
  }
  npv -= residualValue / (1 + monthlyRate) ** months
  return npv
}

function monthlyToAnnualTaegPercent(monthlyRate) {
  const annualPct = ((1 + monthlyRate) ** 12 - 1) * 100
  if (!Number.isFinite(annualPct) || annualPct > 999 || annualPct < -1e-6) return NaN
  return round2(Math.min(99, Math.max(0, annualPct)))
}

/**
 * Sans assurance (>0) incluse au flux, TAEG = taux nominal annuel.
 * Avec assurance : dichotomie sur t mensuel ∈ [0.0001, 0.02], tolérance 1e-6, max 100 itérations.
 */
function computeTaeg(financedAmount, monthlyPayment, months, residualValue, nominalAnnualPercent, insuranceMonthly) {
  const fallbackNominal = () => {
    const n = Number(nominalAnnualPercent)
    if (!Number.isFinite(n) || n < 0) return 0
    return round2(Math.min(99, Math.max(0, n)))
  }

  if (financedAmount <= EPS) return fallbackNominal()
  if (monthlyPayment <= EPS && residualValue <= EPS) return fallbackNominal()

  if (!Number.isFinite(insuranceMonthly) || insuranceMonthly <= EPS) {
    return fallbackNominal()
  }

  const pay = monthlyPayment + insuranceMonthly
  const npv = (t) => npvBorrower(t, financedAmount, pay, months, residualValue)

  let lo = 0.0001
  let hi = 0.02
  let vLo = npv(lo)
  let vHi = npv(hi)

  if (!Number.isFinite(vLo) || !Number.isFinite(vHi)) return fallbackNominal()

  let expanded = 0
  while (vHi <= 0 && hi < 0.5 && expanded++ < 50) {
    hi = Math.min(0.5, hi * 1.4)
    vHi = npv(hi)
  }
  expanded = 0
  while (vLo >= 0 && lo > 1e-7 && expanded++ < 50) {
    lo *= 0.5
    vLo = npv(lo)
  }

  if (!(vLo < 0 && vHi > 0)) return fallbackNominal()

  for (let iter = 0; iter < 100; iter++) {
    const mid = (lo + hi) / 2
    const vm = npv(mid)
    if (!Number.isFinite(vm)) return fallbackNominal()
    if (Math.abs(vm) < 1e-6) {
      lo = hi = mid
      break
    }
    if (vm > 0) hi = mid
    else lo = mid
  }

  const monthlyEff = (lo + hi) / 2
  const annual = monthlyToAnnualTaegPercent(monthlyEff)
  if (Number.isNaN(annual)) return fallbackNominal()
  return annual
}

/**
 * C = prix − acompte ; VR_pv = VR/(1+i)^n ; C_amortir = C − VR_pv ; M = annuité(C_amortir).
 * Intérêts totaux = M×n + VR − C.
 */
export function calculateLeasing(input) {
  const {
    vehiclePrice,
    downPayment,
    durationMonths,
    nominalRate,
    residualValue,
    insuranceMonthly = 0,
  } = input || {}

  const price = Number(vehiclePrice)
  const deposit = Number(downPayment)
  const months = Number(durationMonths)
  const rate = Number(nominalRate)
  const residual = Number(residualValue)
  const casco = Number(insuranceMonthly || 0)

  if (!Number.isFinite(price) || price <= 0) throw new Error('vehiclePrice must be > 0')
  if (!Number.isFinite(deposit) || deposit < 0) throw new Error('downPayment must be >= 0')
  if (!Number.isFinite(months) || ![24, 36, 48, 60].includes(months)) throw new Error('durationMonths invalid')
  if (!Number.isFinite(rate) || rate < 0) throw new Error('nominalRate must be >= 0')
  if (!Number.isFinite(residual) || residual < 0) throw new Error('residualValue must be >= 0')
  if (!Number.isFinite(casco) || casco < 0) throw new Error('insuranceMonthly must be >= 0')
  if (rate > MAX_RATE_LCC) throw new Error(`nominalRate exceeds MAX_RATE_LCC (${MAX_RATE_LCC}%)`)
  if (deposit >= price) throw new Error('downPayment must be strictly lower than vehiclePrice')
  if (residual > price) throw new Error('residualValue cannot exceed vehiclePrice')

  const C = price - deposit
  const i = rate / 100 / 12
  const residualPv = Math.abs(i) <= EPS ? residual : residual / (1 + i) ** months
  const capitalToAmortize = Math.max(0, C - residualPv)
  const monthlyPaymentRaw = annuityPayment(capitalToAmortize, i, months)

  let remaining = C
  const breakdown = []
  for (let month = 1; month <= months; month++) {
    let interest = Math.abs(i) <= EPS ? 0 : remaining * i
    let capital = monthlyPaymentRaw - interest
    if (capital < 0) capital = 0
    let nextRemaining = remaining - capital

    if (month === months) {
      capital = Math.max(0, remaining - residual)
      interest = monthlyPaymentRaw - capital
      nextRemaining = residual
    }

    breakdown.push({
      month,
      capital: round2(capital),
      interest: round2(interest),
      remaining: round2(nextRemaining),
    })
    remaining = nextRemaining
  }

  const formuleUtilisee =
    'M = (C - VR/(1+i)^n) * i / (1 - (1+i)^(-n))  avec  C=prix-acompte,  i=nominal%/12,  n=duration mois'

  const monthlyPaymentFinal = round2(monthlyPaymentRaw)
  const totalMonthly = monthlyPaymentFinal * months
  const totalLeasingCostRaw = deposit + totalMonthly
  const totalWithBuybackRaw = totalLeasingCostRaw + residual
  const totalInterestRaw = totalWithBuybackRaw - price
  if (input?.debugAudit) {
    const interetsRecalc = monthlyPaymentFinal * months + residual - C
    // eslint-disable-next-line no-console
    console.log('[leasing audit]', {
      inputs: { vehiclePrice: price, downPayment: deposit, residualValue: residual, durationMonths: months, nominalRate: rate },
      tauxMensuel_i: i,
      capitalInitial_C: C,
      VR_nominale: residual,
      VR_actualisee_calculee: residualPv,
      capital_a_amortir: capitalToAmortize,
      formule_utilisee: formuleUtilisee,
      mensualite_brute_calculee: monthlyPaymentRaw,
      mensualite_apres_arrondi: monthlyPaymentFinal,
      total_mensualites: monthlyPaymentFinal * months,
      interets_recalcules: interetsRecalc,
      totalInterest_retour: round2(totalInterestRaw),
    })
  }

  return {
    monthlyPayment: monthlyPaymentFinal,
    monthlyPaymentWithInsurance: round2(monthlyPaymentRaw + casco),
    taeg: computeTaeg(C, monthlyPaymentRaw, months, residual, rate, casco),
    totalLeasingCost: round2(totalLeasingCostRaw),
    totalWithBuyback: round2(totalWithBuybackRaw),
    financedAmount: round2(C),
    amortizedCapital: round2(capitalToAmortize),
    residualActualized: round2(residualPv),
    totalInterest: round2(totalInterestRaw),
    breakdown,
  }
}

/**
 * VR nominale max pour garder un capital à amortir ≥ minAmortChf (CHF absolu).
 * Évite les mensualités artificiellement faibles quand la VR marché est trop proche du prix.
 */
export function nominalResidualCeilingForLease(vehiclePrice, downPayment, durationMonths, nominalRate, minAmortChf = 1000) {
  const price = Number(vehiclePrice)
  const deposit = Number(downPayment)
  const months = Number(durationMonths)
  const rate = Number(nominalRate)
  const minAmort = Number.isFinite(Number(minAmortChf)) && Number(minAmortChf) > 0 ? Number(minAmortChf) : 1000
  if (!Number.isFinite(price) || price <= 0) return 0
  if (![24, 36, 48, 60].includes(months)) return Math.max(0, Math.floor(price - 1))
  const C = price - deposit
  if (!Number.isFinite(C) || C <= minAmort) return Math.max(0, Math.floor(price - 1))
  const i = rate / 100 / 12
  const capPv = C - minAmort
  if (Math.abs(i) <= EPS) return Math.max(0, Math.floor(price - 1))
  const cap = capPv * (1 + i) ** months
  return Math.max(0, Math.min(Math.floor(cap), Math.floor(price - 1)))
}

function leaseForCompare(vehiclePrice, downPayment, durationMonths, nominalRate, annualKm, residualValue) {
  return calculateLeasing({
    vehiclePrice,
    downPayment,
    durationMonths,
    annualKm: Number(annualKm) || 10000,
    nominalRate,
    residualValue,
    insuranceMonthly: 0,
  })
}

/**
 * Tuner de comparateur leasing (3 passes):
 * 1) plafonnage VR avec capital minimal à amortir réaliste;
 * 2) respect d'un ordre VR strict décroissant avec la durée;
 * 3) imposer M(durée courte) > M(durée longue) (mensualité décroît quand n augmente) en ajustant les VR
 *    dans des bornes réalistes ; flag `mensualite_anomalie` seulement si impossible.
 */
export function tuneLeaseComparisonColumns(columns, opts) {
  const { vehiclePrice, downPayment, nominalRate, annualKm = 10000 } = opts || {}
  const price = Number(vehiclePrice)
  const dep = Number(downPayment)
  const rate = Number(nominalRate)
  const funded = price - dep
  const minAmortBusinessFallback = Math.max(1000, Math.round(price * 0.05))
  const minAmortByDurationPct = {
    24: 0.22,
    36: 0.28,
    48: 0.34,
    60: 0.40,
  }
  const minAmortForDuration = (months) => {
    const pct = minAmortByDurationPct[Number(months)] || 0.05
    const target = Math.max(minAmortBusinessFallback, Math.round(price * pct))
    return Number.isFinite(funded) && funded > 0 ? Math.min(target, Math.max(1000, Math.floor(funded - 1))) : target
  }

  const addFlag = (residual, flag) => {
    if (!residual || !flag) return residual
    const flags = Array.isArray(residual.tuningFlags) ? [...residual.tuningFlags] : []
    if (!flags.includes(flag)) flags.push(flag)
    return { ...residual, tuningFlags: flags }
  }

  const safeLeaseAt = (months, rv, fallbackLease) => {
    try {
      return leaseForCompare(price, dep, months, rate, annualKm, rv)
    } catch {
      return fallbackLease
    }
  }

  const deltaMinGap = (shortCol, longCol) => {
    const monthGap = Math.max(12, Number(longCol.durationMonths) - Number(shortCol.durationMonths))
    const tranches = Math.max(1, monthGap / 12)
    return Math.max(500, Math.round(price * 0.02 * tranches))
  }

  const monthlyAt = (months, rv) => {
    const m = Number(safeLeaseAt(months, rv, { monthlyPayment: NaN }).monthlyPayment)
    return Number.isFinite(m) ? m : NaN
  }

  const applyResidualAndLease = (col, rv) => {
    let r = Math.floor(Number(rv))
    if (!Number.isFinite(r)) r = 1
    r = Math.max(1, Math.min(r, Math.floor(price - 1)))
    const prevFlags = Array.isArray(col.residual?.tuningFlags) ? col.residual.tuningFlags.filter((f) => f !== 'mensualite_anomalie') : []
    col.residual = { ...col.residual, residualValue: r, tuningFlags: prevFlags }
    col.lease = safeLeaseAt(col.durationMonths, r, col.lease)
  }

  const c = columns.map((col) => {
    if (col.error || !col.residual) return col
    const rv = Math.floor(Number(col.residual.residualValue))
    return {
      ...col,
      residual: {
        ...col.residual,
        residualValue: Number.isFinite(rv) ? rv : 0,
        tuningFlags: Array.isArray(col.residual.tuningFlags) ? [...col.residual.tuningFlags] : [],
      },
    }
  })
  const ok = c.filter((col) => !col.error && col.residual && col.lease).sort((a, b) => a.durationMonths - b.durationMonths)
  if (ok.length < 2) return c

  // Passe 1 : plafonnage par capital minimal réaliste, progressif avec la durée.
  // Objectif commercial: éviter les loyers symboliques dus à une VR trop proche du capital financé.
  for (const col of ok) {
    const months = col.durationMonths
    const cap = nominalResidualCeilingForLease(price, dep, months, rate, minAmortForDuration(months))
    let rv = Math.floor(Number(col.residual.residualValue))
    if (!Number.isFinite(rv)) rv = 0
    rv = Math.min(rv, cap, Math.floor(price - 1))
    rv = Math.max(1, rv)
    col.residual = { ...col.residual, residualValue: rv, tuningFlags: [] }
    col.lease = safeLeaseAt(months, rv, col.lease)
  }

  // Passe 2 : VR strictement décroissantes avec la durée (invariant marché)
  for (let k = 0; k < ok.length - 1; k++) {
    const shortCol = ok[k]
    const longCol = ok[k + 1]
    const shortRv = Math.floor(Number(shortCol.residual?.residualValue))
    const longRv = Math.floor(Number(longCol.residual?.residualValue))
    if (shortRv > longRv) continue

    const delta = deltaMinGap(shortCol, longCol)
    let forcedRv = Math.floor(longRv + delta)
    if (!Number.isFinite(forcedRv)) forcedRv = longRv + 500
    if (forcedRv >= price) forcedRv = Math.max(1, Math.floor(price - 1))

    const shortCap = nominalResidualCeilingForLease(price, dep, shortCol.durationMonths, rate, minAmortForDuration(shortCol.durationMonths))
    if (forcedRv > shortCap) {
      shortCol.residual = addFlag(shortCol.residual, 'vr_anomalie')
    }

    shortCol.residual = { ...shortCol.residual, residualValue: forcedRv }
    shortCol.lease = safeLeaseAt(shortCol.durationMonths, forcedRv, shortCol.lease)
  }

  // Passe 3 : M(court) > M(long) — ajustements VR bornés (itérations jusqu'à stabilité)
  const EPS_M = 0.02
  /** Écart minimal (CHF) pour un ordre commercialement lisible des mensualités avec n croissant. */
  const M_GAP = Math.max(25, Math.round(price * 0.0005))
  const tryRaiseShortPayment = (shortCol, longCol) => {
    const moS = shortCol.durationMonths
    let shortRv = Math.floor(Number(shortCol.residual?.residualValue))
    const longRv = Math.floor(Number(longCol.residual?.residualValue))
    const mL = Number(longCol.lease?.monthlyPayment)
    if (!Number.isFinite(shortRv) || !Number.isFinite(longRv) || !Number.isFinite(mL)) return false
    const delta = deltaMinGap(shortCol, longCol)
    const shortFloor = longRv + delta
    const capS = nominalResidualCeilingForLease(price, dep, moS, rate, minAmortForDuration(moS))
    const hi = Math.min(shortRv, capS, Math.floor(price - 1))
    if (hi < shortFloor) return false
    const target = mL + M_GAP
    const mAtFloor = monthlyAt(moS, shortFloor)
    if (!Number.isFinite(mAtFloor) || mAtFloor < target - EPS_M) return false
    let lo = shortFloor
    let hiB = hi
    while (lo < hiB) {
      const mid = Math.floor((lo + hiB + 1) / 2)
      const mm = monthlyAt(moS, mid)
      if (Number.isFinite(mm) && mm >= target - EPS_M) lo = mid
      else hiB = mid - 1
    }
    applyResidualAndLease(shortCol, lo)
    return true
  }

  const tryLowerLongPayment = (shortCol, longCol) => {
    const moL = longCol.durationMonths
    const shortRv = Math.floor(Number(shortCol.residual?.residualValue))
    let longRv = Math.floor(Number(longCol.residual?.residualValue))
    const mS = Number(shortCol.lease?.monthlyPayment)
    if (!Number.isFinite(shortRv) || !Number.isFinite(longRv) || !Number.isFinite(mS)) return false
    const delta = deltaMinGap(shortCol, longCol)
    const longCeiling = Math.min(
      nominalResidualCeilingForLease(price, dep, moL, rate, minAmortForDuration(moL)),
      Math.floor(price - 1),
      shortRv - delta - 1
    )
    if (longRv >= longCeiling) return false
    const target = mS - M_GAP
    const mAtCeil = monthlyAt(moL, longCeiling)
    if (!Number.isFinite(mAtCeil) || mAtCeil > target + EPS_M) return false
    let lo = longRv + 1
    let hiB = longCeiling
    while (lo < hiB) {
      const mid = Math.floor((lo + hiB) / 2)
      const mm = monthlyAt(moL, mid)
      if (Number.isFinite(mm) && mm <= target + EPS_M) hiB = mid
      else lo = mid + 1
    }
    applyResidualAndLease(longCol, lo)
    return true
  }

  const tryBracketFix = (shortCol, longCol) => {
    const moS = shortCol.durationMonths
    const moL = longCol.durationMonths
    const longRv = Math.floor(Number(longCol.residual?.residualValue))
    const delta = deltaMinGap(shortCol, longCol)
    const capS = nominalResidualCeilingForLease(price, dep, moS, rate, minAmortForDuration(moS))
    const capL = nominalResidualCeilingForLease(price, dep, moL, rate, minAmortForDuration(moL))
    const shortTarget = Math.max(1, Math.min(longRv + delta, capS, Math.floor(price - 1)))
    const longTop = Math.min(capL, Math.floor(price - 1), shortTarget - delta - 1)
    if (longTop < 1 || longTop <= longRv) return false
    applyResidualAndLease(shortCol, shortTarget)
    applyResidualAndLease(longCol, longTop)
    return true
  }

  for (let round = 0; round < 40; round++) {
    let touched = false
    for (let k = 0; k < ok.length - 1; k++) {
      const shortCol = ok[k]
      const longCol = ok[k + 1]
      const mShort = Number(shortCol.lease?.monthlyPayment)
      const mLong = Number(longCol.lease?.monthlyPayment)
      if (!Number.isFinite(mShort) || !Number.isFinite(mLong)) continue
      if (mShort >= mLong + M_GAP - EPS_M) continue
      if (tryRaiseShortPayment(shortCol, longCol)) {
        touched = true
        continue
      }
      if (tryLowerLongPayment(shortCol, longCol)) {
        touched = true
        continue
      }
      if (tryBracketFix(shortCol, longCol)) {
        touched = true
        continue
      }
    }
    if (!touched) break
  }

  for (let k = 0; k < ok.length - 1; k++) {
    const shortCol = ok[k]
    const longCol = ok[k + 1]
    const mShort = Number(shortCol.lease?.monthlyPayment)
    const mLong = Number(longCol.lease?.monthlyPayment)
    if (!Number.isFinite(mShort) || !Number.isFinite(mLong)) continue
    if (mShort < mLong + M_GAP - EPS_M) {
      shortCol.residual = addFlag(shortCol.residual, 'mensualite_anomalie')
    }
  }

  return c
}
